import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UpdateProcurementSupplierDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_PROCUREMENT } from "../domain";
import { ACTION_GET_PROCUREMENT_SUPPLIERS } from "../GetProcurementSuppliers";

import { procurementApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_PROCUREMENT_SUPPLIER = "UPDATE_PROCUREMENT_SUPPLIER";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type UpdateProcurementSupplier = {
  procurementSupplierId: string;
  params: UpdateProcurementSupplierDto;
};

const updateProcurementSupplier = async (
  props: UpdateProcurementSupplier,
): Promise<void> => {
  const response =
    await procurementApi.procurementControllerUpdateProcurementSupplier(
      props.procurementSupplierId,
      props.params,
    );

  return response.data;
};

export const useUpdateProcurementSupplier = (
  user: UserProfileDto | null,
): {
  updateProcurementSupplier: (props: UpdateProcurementSupplier) => void;
  updateProcurementSupplierAsync: (
    props: UpdateProcurementSupplier,
  ) => Promise<void>;
  updateProcurementSupplierEnabled: boolean;
  isSuccessUpdateProcurementSupplier: boolean;
  isPendingUpdateProcurementSupplier: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_PROCUREMENT, ACTION_UPDATE_PROCUREMENT_SUPPLIER],
    mutationFn: enabled ? updateProcurementSupplier : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_PROCUREMENT, ACTION_GET_PROCUREMENT_SUPPLIERS],
      });
    },
  });

  return {
    updateProcurementSupplier: mutate,
    updateProcurementSupplierAsync: mutateAsync,
    updateProcurementSupplierEnabled: enabled,
    isSuccessUpdateProcurementSupplier: isSuccess,
    isPendingUpdateProcurementSupplier: isPending,
  };
};
